import styled, { css } from 'styled-components';

export const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
  padding-bottom: 24px;
`;

type Accent = 'whatsapp' | 'public' | 'reserved';

function accentColors(theme: any, accent: Accent) {
  const map: Record<Accent, { bg: string; border: string; text: string }> = {
    whatsapp: {
      bg: 'transparent',
      border: theme.colors.positive.primary,
      text: theme.colors.positive.primaryAlt,
    },
    public: {
      bg: 'transparent',
      border: theme.colors.divider.primary,
      // Same tone as the "Todos veem" public tag.
      text: theme.colors.alert.primaryAlt,
    },
    reserved: {
      bg: 'transparent',
      border: theme.colors.divider.primary,
      text: theme.colors.element.secondary,
    },
  };
  return map[accent];
}

export const HeaderCard = styled.div<{ accent: Accent }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  ${({ theme, accent }) => {
    const c = accentColors(theme, accent);
    // Headers have no tinted background, so they also drop the left accent bar.
    const plain = accent === 'public' || accent === 'reserved' || accent === 'whatsapp';
    return css`
      background: ${c.bg};
      ${plain ? '' : `border-left: 3px solid ${c.border};`}
      ${plain ? 'padding-left: 0;' : ''}
    `;
  }}
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  > svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }
`;

// Yellow icon chip for the public response header; the megaphone matches the "Todos veem" tag color.
export const PublicIconChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.alert.secondary};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.alert.primaryAlt};
  }
`;

// Green icon chip for the WhatsApp header (square, like the other header chips).
export const WhatsAppIconChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.positive.primary};
  flex-shrink: 0;

  svg {
    width: 22px;
    height: 22px;
    color: ${({ theme }) => theme.colors.positive.overPrimary};
  }
`;

// Gray icon chip for the reserved message header; the lock matches the "Só o consumidor" tag color.
export const ReservedIconChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.element.secondary};
  }
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const HeaderTitle = styled.span<{ accent: Accent }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme, accent }) => accentColors(theme, accent).text};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

export const HeaderSubtitle = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.element.secondary};
`;

export const SectionTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.element.primary};
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const CharCounter = styled.span<{ error?: boolean }>`
  align-self: flex-end;
  font-size: 12px;
  color: ${({ theme, error }) => (error ? theme.colors.negative.primary : theme.colors.element.placeholder)};
`;

export const HintBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.element.secondary};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

export const PreviewLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.element.placeholder};
`;

export const WhatsappBubble = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1.5px solid ${({ theme }) => theme.colors.divider.primary};
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.element.primary};
`;

export const ConfirmBox = styled.div<{ checked?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  transition: all 0.15s ease;
  ${({ theme, checked, disabled }) => {
    if (disabled) {
      // Public message is empty: confirmation is not applicable yet, shown grayed out.
      return css`
        border: 1.5px solid ${theme.colors.divider.primary};
        background: ${theme.colors.background.tertiary};
        opacity: 0.7;
        span {
          color: ${theme.colors.element.placeholder};
        }
      `;
    }
    return css`
      border: 1.5px solid ${checked ? theme.colors.positive.primary : theme.colors.brand.primary};
      background: ${checked ? theme.colors.positive.secondary : theme.colors.brand.primary};
      span {
        color: ${checked ? theme.colors.element.primary : theme.colors.brand.overPrimary};
      }
      ${!checked &&
      css`
        /* Desmarcado, o box é um brand.primary sólido e a borda rosa do checkbox some.
           Usa a cor "sobre o brand" (mais escura, igual ao texto) para o check aparecer. */
        .checkbox > div > div > div {
          border-color: ${theme.colors.brand.overPrimary};
        }
      `}
    `;
  }}

  span {
    font-size: 13px;
    line-height: 1.4;
  }

  b {
    font-weight: 700;
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 8px;
`;

export const FooterHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.element.placeholder};
  flex: 1;
`;

/* ---------- Review ---------- */

export const ReviewBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ReviewBlockTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.element.primary};
`;

export const ReviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;

  span:first-child {
    color: ${({ theme }) => theme.colors.element.placeholder};
  }
  span:last-child {
    color: ${({ theme }) => theme.colors.element.primary};
    font-weight: 500;
    text-align: right;
  }
`;

export const MessageCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.divider.primary};
  border-radius: 8px;
  overflow: hidden;
`;

export const MessageCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.background.secondary};
`;

export const MessageCardTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.element.primary};
`;

export const MessageCardBody = styled.div`
  padding: 12px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.element.secondary};
`;
